"use client";

import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import type { ChannelBadge, ColumnDef, DataTableDensity, DataTableProps, ServerTableState } from "./types";
import { useDataTableState } from "./useDataTableState";
import { renderFormattedCell } from "./formatters";
import { buildCsv, downloadCsv } from "./csv";
import { ColumnSettingsModal } from "./ColumnSettingsModal";
import { Button } from "../components/Button";
import { useInjectStyles } from "../utils/useInjectStyles";

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];
/**
 * `filterRange` 欄位把「起~迄」編成同一個字串時用的分隔字元。
 * 選 `~` 而不是逗號／連字號：日期是 `2026-01-01`（含 `-`）、數字可能是負數，
 * 兩者都會讓那些分隔字元變成有歧義的切點。
 */
export const RANGE_SEP = "~";
const DENSITY_STORAGE_KEY = "ccheng_dt_density";

/**
 * 依 format／kind 決定對齊：數字、金額、序號靠右（並用等寬數字讓位數對齊掃視），
 * 布林與動作欄置中／靠右，其餘靠左。column.align 顯式指定時以它為準。
 */
function alignClassOf<T>(col: ColumnDef<T>): string {
  if (col.align === "right") return " cui-dt-align-right";
  if (col.align === "center") return " cui-dt-align-center";
  if (col.align === "left") return "";
  const kind = col.kind ?? "data";
  if (kind === "serial") return " cui-dt-align-right";
  if (kind === "action") return " cui-dt-align-right";
  if (kind === "checkbox") return " cui-dt-align-center";
  if (col.format === "currency") return " cui-dt-align-right";
  if (col.format === "boolean") return " cui-dt-align-center";
  return "";
}

/** 通道徽章的預設文案。text 有給就用給的。 */
function channelTextOf(badge: ChannelBadge, total: number, hasTotal: boolean): string {
  if (badge.text) return badge.text;
  switch (badge.channel) {
    case "s":
      return hasTotal ? `⚡ 可即時查 · 共 ${total.toLocaleString("zh-Hant-TW")} 筆` : "⚡ 可即時查";
    case "c":
      return "📌 請先圈定範圍";
    case "idx":
      return "🔍 深度搜尋 · 部分欄位可篩";
    case "bq":
      return "📊 資料為 T+1 批次";
    default:
      return "";
  }
}

function renderCell<T>(column: ColumnDef<T>, row: T, rowIndex: number, pageStartIndex: number): ReactNode {
  const kind = column.kind ?? "data";
  if (kind === "serial") return pageStartIndex + rowIndex + 1;
  if (kind === "action") {
    const actions = column.actions ?? [];
    return (
      <span className="cui-dt-actions">
        {actions
          .filter((a) => (a.visible ? a.visible(row) : true))
          .map((a) => (
            <Button
              key={a.key}
              size="sm"
              /*
               * 動作鈕預設改為 link（無框主色文字）：清單頁的動作幾乎都是「編輯／檢視／
               * 復原」這類導覽性操作，原本的 ghost 是純黑文字，與同列的紅色「刪除」重量
               * 不對等，看起來像兩套元件。刪除仍由頁面顯式指定 variant:"danger"（也是
               * 無框，但紅字＋hover 才浮現淺紅底）。
               */
              variant={a.variant ?? "link"}
              onClick={() => a.onClick(row)}
            >
              {a.label}
            </Button>
          ))}
      </span>
    );
  }
  return renderFormattedCell(column, row);
}

/**
 * 舊站那排頁碼（業主裁決 26：「補做舊站『共 32,266 項』旁邊那排可直接點的頁碼列」）。
 *
 * 一次畫幾顆：**10 顆**，照搬舊站——後台 GridView 走 `kartik\dynagrid\DynaGrid`，
 * 沒有覆寫 `maxButtonCount`（逐一 grep 過 `legacy/Ccheng_web/admin/views/**`，零命中），
 * 用的就是 Yii `LinkPager::$maxButtonCount` 的預設值 10。
 * ⛔ 先前是 window=1（只畫得出「1 2 … 1614」），從第 1 頁連第 5 頁都點不到——
 * 那不叫「那排頁碼列」。
 *
 * 與舊站的差異（保留，非新增功能）：本元件在視窗兩端另外固定畫出第 1 頁與最後一頁
 * （中間以 `…` 分隔）。舊站 LinkPager 只有滑動視窗、沒有首末頁捷徑；這一顆是既有
 * 元件在 B2/B3 就有的行為，一併留著。
 */
const PAGE_BUTTON_COUNT = 10;

function buildPageList(current: number, pageCount: number): (number | "…")[] {
  // 以目前頁為中心取 10 顆連續頁碼，撞到邊界就往另一側補滿（同 Yii LinkPager 的
  // `getPageRange()`：視窗長度固定，不會在頭尾變短）。
  const span = Math.min(PAGE_BUTTON_COUNT, pageCount);
  let start = Math.max(1, current - Math.floor(span / 2));
  const end = Math.min(pageCount, start + span - 1);
  start = Math.max(1, end - span + 1);

  const pages: (number | "…")[] = [];
  for (let p = 1; p <= pageCount; p++) {
    if (p === 1 || p === pageCount || (p >= start && p <= end)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }
  return pages;
}

export function DataTable<T>({
  columns: columnsProp,
  rows,
  rowKey,
  variant = "admin",
  storageKey,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  defaultPageSize = 20,
  showGlobalSearch = true,
  showColumnFilters = variant === "admin",
  showColumnSettings = variant === "admin",
  showExport = variant === "admin",
  emptyText = "沒有符合條件的資料",
  exportFileName = "export",
  selectedKeys,
  onSelectionChange,
  caption,
  density: densityProp,
  showDensityToggle = variant === "admin",
  channelBadge,
  paginationMode = "numbered",
  loading = false,
  error = null,
  onRetry,
  server,
  rowStyle,
}: DataTableProps<T>) {
  useInjectStyles();

  /*
   * 伺服端分頁模式（WP-D1）。給了 server 就一切以伺服器為準：
   * rows 原樣渲染、篩選/搜尋受控、排序只開放伺服端排得動的欄位、分頁只有前後翻。
   * 沒給就是原本的 S 級全讀行為——既有頁面一行都不用改。
   */
  const isServer = server !== undefined;
  const state = useDataTableState(columnsProp, rows, storageKey, isServer);
  const {
    columns,
    setColumnVisible,
    moveColumn,
    resetColumns,
    sort,
    toggleSort,
    filters,
    setFilter,
    clearAllFilters,
    globalSearch,
    setGlobalSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    total,
    pageRows,
    filteredSortedRows,
  } = state;

  const [settingsOpen, setSettingsOpen] = useState(false);

  /*
   * 「目前生效的」篩選/搜尋/排序：伺服端模式取自 server（受控，由頁面持有），
   * 否則取自元件內部狀態。收斂成這四個常數，下面的 JSX 只認它們，不會出現
   * 「這一段忘了走 server」的漏接。
   */
  const effFilters = isServer ? server.filters ?? {} : filters;
  const effGlobalSearch = isServer ? server.globalSearch ?? "" : globalSearch;
  const effSort = isServer ? server.sort ?? { key: null, direction: null } : sort;
  const setEffFilter = (key: string, value: string) => {
    if (isServer) server.onFilterChange?.(key, value);
    else setFilter(key, value);
  };
  const clearEffFilters = () => {
    if (isServer) server.onClearFilters?.();
    else clearAllFilters();
  };
  const setEffGlobalSearch = (value: string) => {
    if (isServer) {
      server.onGlobalSearchChange?.(value);
      return;
    }
    setPage(1);
    setGlobalSearch(value);
  };
  /** 這一欄伺服端排得動嗎？排不動就不畫排序鈕——畫了點下去只會重排當頁 20 筆。 */
  const serverCanSort = (key: string) =>
    Boolean(server?.onSortChange) && (server?.sortableKeys ?? []).includes(key);

  /*
   * B3 第 7 輪①附註：伺服端模式三段循環（asc→desc→清除）的「清除」是各呼叫端
   * 在自己的 onSortChange 裡決定的（DataTable 不擁有 server.sort 狀態），清除後
   * 常常伺服端仍回報一個「預設排序」欄位/方向（例如清單本來就有 defaultOrder，
   * 使用者清掉排序後資料還是那個欄位的遞增序）——`server.sort` 因此繼續指到同一
   * 欄，讓 <th> 的 aria-sort／圖示殘留 ascending，看起來像「清除沒生效」。
   * 各呼叫端一致採用同一慣例：使用者點的正是「這欄目前是 desc」那一下＝清除。
   * 用這個慣例在本地暫時蓋掉那一欄的顯示，不需要動任何呼叫端或 server.sort 的
   * 型別；使用者對任何欄位再次動作就會解除。
   */
  const [clearedSortKey, setClearedSortKey] = useState<string | null>(null);
  const handleSortClick = useCallback(
    (key: string) => {
      if (isServer) {
        setClearedSortKey(effSort.key === key && effSort.direction === "desc" ? key : null);
        server.onSortChange?.(key);
      } else {
        toggleSort(key);
      }
    },
    [isServer, effSort.key, effSort.direction, server, toggleSort],
  );

  // 密度：受控（densityProp）優先；否則讀 localStorage，跨頁面共用同一偏好。
  const [densityState, setDensityState] = useState<DataTableDensity>("comfortable");
  useEffect(() => {
    if (densityProp) return;
    try {
      const saved = window.localStorage.getItem(DENSITY_STORAGE_KEY);
      if (saved === "compact" || saved === "comfortable") setDensityState(saved);
    } catch {
      /* 無痕視窗／已封鎖站台資料：用預設密度即可 */
    }
  }, [densityProp]);
  const density = densityProp ?? densityState;
  const toggleDensity = useCallback(() => {
    setDensityState((d) => {
      const next: DataTableDensity = d === "compact" ? "comfortable" : "compact";
      try {
        window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
      } catch {
        /* 寫入失敗不影響本次切換 */
      }
      return next;
    });
  }, []);

  // 第 15 輪嚴重修正第 1 條：目前生效的欄位篩選（不受欄位可見性影響）——即使
  // 該欄已被「欄位設定」隱藏（防護：任何未來路徑繞過 setColumnVisible 的清除），
  // 摘要 chip 仍可視、可逐一點掉或一鍵清除，「共 N 筆」異常偏低時有跡可循。
  const activeFilterEntries = Object.entries(effFilters).filter(
    ([, v]) => v !== "" && v !== undefined,
  );
  const filterLabelOf = (key: string) => columns.find((c) => c.key === key)?.label ?? key;

  const visibleColumns = columns.filter((c) => c.visible !== false);
  const effPageSize = isServer ? server.pageSize : pageSize;
  const pageStartIndex = ((isServer ? server.pageIndex : page) - 1) * effPageSize;

  const selected = new Set(selectedKeys ?? []);

  function toggleRowSelected(key: string | number) {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(Array.from(next));
  }

  function toggleAllOnPage() {
    if (!onSelectionChange) return;
    const pageKeys = pageRows.map(rowKey);
    const allSelected = pageKeys.every((k) => selected.has(k));
    const next = new Set(selected);
    if (allSelected) pageKeys.forEach((k) => next.delete(k));
    else pageKeys.forEach((k) => next.add(k));
    onSelectionChange(Array.from(next));
  }

  function handleExport() {
    // 伺服端分頁時手上只有這一頁——按鈕文案（exportLabel）必須把這件事講清楚，
    // 否則使用者會以為匯出的是整份查詢結果。
    const csv = buildCsv(columns, filteredSortedRows);
    downloadCsv(exportFileName, csv);
  }

  const serverTotal = isServer ? server.total ?? null : null;
  /**
   * 「有沒有總筆數」與「能不能跳頁」是**兩件事**，先前綁在同一個 `isCursor` 上。
   *
   * 綁在一起的後果（WP-B3 課程清單接上 `withTotal` 時撞到）：伺服端一旦算得出
   * 總筆數，版面就整組切換成數字頁碼——而那排頁碼按鈕呼叫的是**前端**的
   * `setPage`，在伺服端分頁模式下按了資料不會動。等於「看起來可以跳頁，其實不會」。
   *
   * 拆開之後：
   * - `hasTotal`：算得出來就顯示「共 N 筆」（不再因為是 cursor 分頁就假裝不知道）。
   * - `isCursorPager`：伺服端分頁**一律**只有上一頁／下一頁（Firestore cursor 沒有
   *   跳頁能力），與知不知道總數無關。
   */
  const hasTotal = paginationMode !== "cursor" && (!isServer || serverTotal !== null);
  /**
   * 業主裁決 26（頁碼跳頁「要補」）：伺服端**也**能有數字頁碼了，但只在後端明說
   * 做得到的時候——`server.onJumpToPage` 有給，才代表那一頁的後端接受 `page=N`
   * （判準在後端 `counter_service.supports_page_jump()`：精確總筆數 + 非 BQ 通道）。
   * 沒給就維持上一頁／下一頁，與 `bcba223` 拆開 hasTotal/isCursorPager 的理由一致：
   * ⛔ 絕不畫「看起來能跳、按了資料不動」的頁碼列。
   */
  const serverPageCount = isServer ? server.pageCount ?? null : null;
  const canServerJump = Boolean(
    isServer && server.onJumpToPage && hasTotal && serverPageCount !== null
  );
  const isCursorPager = (paginationMode === "cursor" || isServer) && !canServerJump;
  /**
   * 「共有幾頁」與「點得到第幾頁」是兩件事（後端 `maxJumpPage`，目前 500）。
   * 訂單清單共 19,539 頁，但深頁碼是一頁一頁推算出來的，超過上限後端一律拒絕——
   * ⛔ 照 `pageCount` 畫出「最後一頁 19539」等於畫了一顆按下去只會出錯的按鈕。
   * 頁碼列的右界因此取 `min(pageCount, maxJumpPage)`，差距則在頁尾講給使用者聽。
   */
  const serverMaxJump = isServer ? server.maxJumpPage ?? null : null;
  const jumpablePageCount =
    serverPageCount !== null && serverMaxJump !== null
      ? Math.min(serverPageCount, serverMaxJump)
      : serverPageCount;
  //「現在第幾頁 / 點得到幾頁 / 怎麼跳」三者必須同源，否則高亮的頁碼會跟資料對不起來。
  const curPage = canServerJump && server ? server.pageIndex : page;
  const curPageCount = canServerJump ? Math.max(1, jumpablePageCount ?? 1) : pageCount;
  /** 總頁數比點得到的頁數多時要主動說明，否則使用者只會覺得「後面的資料不見了」。 */
  const jumpCapped =
    canServerJump && serverPageCount !== null && serverMaxJump !== null && serverPageCount > serverMaxJump;
  const jumpTo = (target: number) => {
    const clamped = Math.min(Math.max(1, target), curPageCount);
    if (clamped === curPage) return;
    if (canServerJump && server) server.onJumpToPage?.(clamped);
    else setPage(clamped);
  };

  /*
   * 頁尾筆數文字＝舊站 GridView 的 summary，**逐字**照搬 Yii 內建 zh-TW 翻譯
   * （`legacy/Ccheng_web/vendor/yiisoft/yii2/messages/zh-TW/yii.php:48`）：
   *   '第 <b>{begin, number}-{end, number}</b> 項，共 <b>{totalCount, number}</b> 項資料.'
   * ——量詞是「項」不是「筆」、範圍在前總數在後、連字號是半形 `-`、句尾有半形句點。
   * ⛔ 先前寫的是自造的「共 N 筆，第 a–b 筆」（順序相反、量詞不同、全形 en-dash、
   *   沒有句點），與舊站四處對不上（裁決 23／26 第 1 輪②重要 2）。
   * `用語對照表.md` 全表沒有這一條（表內只有 app 分類的翻譯，這句在 yii 分類），
   * 因此依「沒有條目就照舊站原文逐字」處理。
   *
   * begin/end 的算法同樣照 `BaseListView::renderSummary()`：end = begin + 本頁筆數 - 1
   * （⛔ 不是 begin + 每頁筆數 - 1，最後一頁會多算），且本頁 0 筆時整句不顯示。
   */
  const summaryTotal = serverTotal ?? total;
  const summaryCount = pageRows.length;
  const summaryBegin = pageStartIndex + 1;
  const summaryEnd = pageStartIndex + summaryCount;

  /** 表格前的「跳到分頁」跳連結要指得到頁碼列；同頁可能有多個 DataTable，用 useId 保證唯一。 */
  // useId() 產出的 `:r1:` 含冒號，當 CSS 選擇器用會炸掉；只留可安全出現在 id 的字元。
  const pagerId = `cui-dt-pager-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  return (
    <div className="cui-reset cui-dt-wrap">
      {channelBadge && (
        <div style={{ marginBottom: "var(--cui-space-3)" }}>
          <span className={`cui-channel cui-channel-${channelBadge.channel}`}>
            {channelTextOf(channelBadge, serverTotal ?? total, hasTotal)}
          </span>
        </div>
      )}

      <div className="cui-dt-card">
      {(showGlobalSearch || showColumnSettings || showExport || showDensityToggle) && (
        <div className="cui-dt-toolbar">
          <div className="cui-dt-toolbar-left">
            {showGlobalSearch && (
              // 放大鏡圖示：側欄搜尋框有、卡片內搜尋框原本沒有，站內兩個搜尋框長得不一樣
              <span className="cui-dt-search-wrap">
              <span className="cui-dt-search-icon" aria-hidden>🔍</span>
              <input
                type="search"
                className="cui-dt-search"
                placeholder="搜尋…"
                value={effGlobalSearch}
                onChange={(e) => setEffGlobalSearch(e.target.value)}
                aria-label="搜尋全部欄位"
              />
              </span>
            )}
          </div>
          <div className="cui-dt-toolbar-right">
            {showDensityToggle && (
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleDensity}
                aria-label={density === "compact" ? "切換為舒適密度" : "切換為緊湊密度"}
                title={density === "compact" ? "目前：緊湊。點擊切換為舒適" : "目前：舒適。點擊切換為緊湊"}
              >
                ▦ {density === "compact" ? "緊湊" : "舒適"}
              </Button>
            )}
            {showExport && (
              <Button variant="secondary" size="sm" onClick={handleExport}>
                {isServer ? server.exportLabel ?? "匯出本頁 CSV" : "匯出 CSV"}
              </Button>
            )}
            {showColumnSettings && (
              <Button variant="secondary" size="sm" onClick={() => setSettingsOpen(true)}>
                欄位設定
              </Button>
            )}
          </div>
        </div>
      )}

      {activeFilterEntries.length > 0 && (
        // 第 15 輪嚴重修正第 1 條：常駐篩選摘要列——顯示「已套用 N 個篩選」與各
        // 篩選 chip（欄位標籤：條件值，點 ✕ 清除單一條件），並提供「清除全部篩選」。
        // 不受欄位可見性影響：欄被隱藏時篩選輸入框雖消失，這裡仍看得到、清得掉。
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 6,
            fontSize: 12,
            color: "var(--cui-text-muted, #666)",
            padding: "4px 2px",
          }}
        >
          <span>已套用 {activeFilterEntries.length} 個篩選：</span>
          {activeFilterEntries.map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => setEffFilter(key, "")}
              title="清除此篩選"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                border: "1px solid var(--cui-border, #ccc)",
                borderRadius: 999,
                padding: "1px 8px",
                background: "transparent",
                color: "inherit",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {filterLabelOf(key)}：{value}
              <span aria-hidden="true">✕</span>
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={clearEffFilters}>
            清除全部篩選
          </Button>
        </div>
      )}

      {/*
        「跳到分頁」跳連結（視覺隱藏，Tab 到才浮現）。
        純鍵盤操作者要走到頁碼列，得先 Tab 過整批資料列——每列有勾選框、連結、
        動作鈕，20 列疊起來就是上百個停駐點（裁決 23／26 第 1 輪②次要 8）。
        放在表格**之前**才有意義：跳過的正是後面那一大段。
      */}
      <a href={`#${pagerId}`} className="cui-visually-hidden cui-dt-skiplink">
        跳到分頁
      </a>

      <div className="cui-dt-scroll">
        <table className={`cui-dt-table cui-variant-${variant} cui-density-${density}`}>
          {caption && <caption style={{ captionSide: "top", textAlign: "left", padding: "6px 2px" }}>{caption}</caption>}
          <thead>
            <tr>
              {visibleColumns.map((col) => {
                const kind = col.kind ?? "data";
                if (kind === "checkbox") {
                  return (
                    <th key={col.key} style={{ width: col.width ?? "32px" }}>
                      <input
                        type="checkbox"
                        aria-label="全選本頁"
                        checked={pageRows.length > 0 && pageRows.every((r) => selected.has(rowKey(r)))}
                        onChange={toggleAllOnPage}
                      />
                    </th>
                  );
                }
                // 不可篩選的資料欄在表頭掛小鎖：主動說明「這欄不能篩，不是系統忘了做」，
                // 減少使用者把設計限制誤報成 bug。
                const showLock = showColumnFilters && kind === "data" && col.filterable === false;
                const lock = showLock ? (
                  <span className="cui-dt-lock-icon" title="此欄不支援篩選" aria-label="此欄不支援篩選">
                    🔒
                  </span>
                ) : null;
                // 伺服端模式：只有伺服器排得動的欄位才給排序鈕。排不動卻畫了鈕，
                // 點下去只會把當頁重排——那是「看起來排了整份、其實只排了 20 筆」。
                const sortable =
                  kind === "data" &&
                  col.sortable !== false &&
                  (!isServer || serverCanSort(col.key));
                if (!sortable) {
                  return (
                    <th key={col.key} className={alignClassOf(col)} style={{ width: col.width }}>
                      {col.label}
                      {lock}
                    </th>
                  );
                }
                // 伺服端模式清除排序後（見上方 clearedSortKey 說明），這欄暫時強制
                // 視為未排序，不受 server.sort 殘留同一欄的影響。
                const isActive =
                  effSort.key === col.key && !(isServer && clearedSortKey === col.key);
                // B2 瀏覽器稽核（低）：排序鍵盤可操作（按鈕本身可 Tab/Enter），但 <th>
                // 沒有 aria-sort——螢幕閱讀器聽不出這欄目前排序方向。ascending／
                // descending／none 三態直接對應 effSort，未排序的欄一律 "none"（不給
                // aria-sort 也可以，但明講 "none" 讓輔助技術能區分「這欄可排序、只是
                // 現在沒排」與「這欄本來就不能排序」，故一律加上，不省略）。
                const ariaSort: "ascending" | "descending" | "none" =
                  isActive && effSort.direction === "asc"
                    ? "ascending"
                    : isActive && effSort.direction === "desc"
                      ? "descending"
                      : "none";
                return (
                  <th key={col.key} className={alignClassOf(col)} style={{ width: col.width }} aria-sort={ariaSort}>
                    <button
                      type="button"
                      className="cui-dt-th-btn"
                      onClick={() => handleSortClick(col.key)}
                    >
                      {col.label}
                      <span className={`cui-dt-sort-icon${isActive && effSort.direction ? " cui-active" : ""}`}>
                        {isActive && effSort.direction === "asc" ? "▲" : isActive && effSort.direction === "desc" ? "▼" : "↕"}
                      </span>
                    </button>
                    {lock}
                  </th>
                );
              })}
            </tr>
            {showColumnFilters && (
              <tr className="cui-dt-filter-row">
                {visibleColumns.map((col) => {
                  const kind = col.kind ?? "data";
                  // 不可篩的資料欄放一個「—」而非留白：留白會讓人以為輸入框沒渲染出來
                  if (kind === "data" && col.filterable === false) {
                    return (
                      <th key={col.key}>
                        <span className="cui-dt-filter-na" aria-hidden>
                          —
                        </span>
                      </th>
                    );
                  }
                  if (kind !== "data") return <th key={col.key} />;
                  if (col.filterOptions) {
                    return (
                      <th key={col.key}>
                        <select
                          className="cui-dt-filter-select"
                          value={effFilters[col.key] ?? ""}
                          onChange={(e) => setEffFilter(col.key, e.target.value)}
                          aria-label={`依 ${col.label} 篩選`}
                        >
                          <option value="">全部</option>
                          {col.filterOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </th>
                    );
                  }
                  const inputType = col.filterKind ?? "text";
                  if (col.filterRange) {
                    // 區間：兩個輸入框，值以 `"起~迄"` 編碼回同一個 filters[key]。
                    const [from = "", to = ""] = (effFilters[col.key] ?? "").split(RANGE_SEP);
                    const emit = (nextFrom: string, nextTo: string) =>
                      setEffFilter(col.key, nextFrom || nextTo ? `${nextFrom}${RANGE_SEP}${nextTo}` : "");
                    return (
                      <th key={col.key}>
                        <span className="cui-dt-filter-range">
                          <input
                            type={inputType}
                            className="cui-dt-filter-input"
                            value={from}
                            onChange={(e) => emit(e.target.value, to)}
                            aria-label={`依 ${col.label} 篩選（起）`}
                          />
                          <input
                            type={inputType}
                            className="cui-dt-filter-input"
                            value={to}
                            onChange={(e) => emit(from, e.target.value)}
                            aria-label={`依 ${col.label} 篩選（迄）`}
                          />
                        </span>
                      </th>
                    );
                  }
                  return (
                    <th key={col.key}>
                      <input
                        type={inputType}
                        className="cui-dt-filter-input"
                        value={effFilters[col.key] ?? ""}
                        onChange={(e) => setEffFilter(col.key, e.target.value)}
                        aria-label={`依 ${col.label} 篩選`}
                      />
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {/* 載入中：skeleton 列。表頭與工具列保持渲染，畫面不整塊閃爍。 */}
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {visibleColumns.map((col) => (
                    <td key={col.key}>
                      <span className="cui-dt-skeleton" style={{ display: "block", width: `${60 + ((i * 13) % 35)}%` }} />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && error && (
              <tr>
                <td colSpan={visibleColumns.length} className="cui-dt-error">
                  <span className="cui-dt-error-msg">{error}</span>
                  {onRetry && (
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      重試
                    </Button>
                  )}
                </td>
              </tr>
            )}
            {!loading && !error && pageRows.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="cui-dt-empty">
                  <span className="cui-dt-empty-icon" aria-hidden>
                    {activeFilterEntries.length > 0 || effGlobalSearch ? "🔍" : "📭"}
                  </span>
                  {/* 字串照舊套標題樣式（向後相容）；給了 ReactNode 就原樣輸出，
                      讓呼叫端自己決定哪段是 .cui-dt-empty-title、哪段是 .cui-dt-empty-hint。 */}
                  {typeof emptyText === "string" ? (
                    <span className="cui-dt-empty-title">{emptyText}</span>
                  ) : (
                    emptyText
                  )}
                  {/* 有篩選條件時給出下一步，而不是只說「沒有資料」 */}
                  {(activeFilterEntries.length > 0 || effGlobalSearch) && (
                    <span className="cui-dt-empty-hint">
                      試著清除篩選條件{" "}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          clearEffFilters();
                          setEffGlobalSearch("");
                        }}
                      >
                        清除全部篩選
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            )}
            {!loading && !error && pageRows.map((row, rowIndex) => {
              const key = rowKey(row);
              return (
                <tr key={key} style={rowStyle?.(row)}>
                  {visibleColumns.map((col) => {
                    if (col.kind === "checkbox") {
                      return (
                        <td key={col.key} className={alignClassOf(col)}>
                          <input
                            type="checkbox"
                            checked={selected.has(key)}
                            onChange={() => toggleRowSelected(key)}
                            aria-label="選取此列"
                          />
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className={alignClassOf(col)}>
                        {renderCell(col, row, rowIndex, pageStartIndex)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="cui-dt-footer">
        {/*
          算不出總數就不假裝有：Firestore cursor 分頁常常拿不到 count，顯示
          「共 N 筆」等於騙人。改為只講本頁載入了幾筆，並明說無法得知總數。
          反過來，伺服器**真的**算得出來（`server.total`）時就照實顯示——那不是
          假裝，是舊站頁尾本來就有的那個數字。
        */}
        <span className="cui-dt-total">
          {loading && pageRows.length === 0 ? (
            // 筆數要等資料真的到了才能講：點進來第一秒 rows 還是空陣列，
            // 這裡若照常渲染就會先寫死「已載入本頁 0 筆」／「共 0 項」，與旁邊還在跑的
            // skeleton 列自相矛盾，使用者很容易讀成「查無資料」（裁決 23／26 第 1 輪②次要 5）。
            <>載入中…</>
          ) : !hasTotal ? (
            <>
              {isServer && <>第 {server.pageIndex.toLocaleString("zh-Hant-TW")} 頁 · </>}
              已載入本頁 <b>{pageRows.length.toLocaleString("zh-Hant-TW")}</b> 筆（無法得知總數）
            </>
          ) : (
            summaryCount > 0 && (
              <>
                第 <b>{summaryBegin.toLocaleString("zh-Hant-TW")}-{summaryEnd.toLocaleString("zh-Hant-TW")}</b> 項，共{" "}
                <b>{summaryTotal.toLocaleString("zh-Hant-TW")}</b> 項資料.
              </>
            )
          )}
        </span>
        <div className="cui-dt-pager" id={pagerId} tabIndex={-1}>
          <select
            className="cui-dt-page-size"
            value={effPageSize}
            onChange={(e) =>
              isServer
                ? server.onPageSizeChange?.(Number(e.target.value))
                : setPageSize(Number(e.target.value))
            }
            aria-label="每頁筆數"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n} 筆/頁
              </option>
            ))}
          </select>
          {isCursorPager ? (
            // cursor／伺服端分頁沒有數字頁碼可跳，只留文字化的前後翻。
            // 伺服端模式的「還有沒有下一頁」由伺服器的 hasMore 決定，不是猜的。
            <>
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={isServer ? server.pageIndex <= 1 : page <= 1}
                onClick={() => (isServer ? server.onPrevPage() : setPage(page - 1))}
              >
                ‹ 上一頁
              </button>
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={isServer ? !server.hasMore : page >= pageCount}
                onClick={() => (isServer ? server.onNextPage() : setPage(page + 1))}
              >
                下一頁 ›
              </button>
            </>
          ) : (
            /*
              數字頁碼列。兩種來源共用同一段 JSX：
              - 前端整批分頁：`page`/`pageCount` 由 useDataTableState 算，跳頁＝setPage。
              - 伺服端跳頁（裁決 26）：`server.pageIndex`/`server.pageCount` 由 API 給，
                跳頁＝`server.onJumpToPage(n)`，由頁面重發一次帶 `page=n` 的查詢。
              ⚠️ 兩者的「現在第幾頁」與「共幾頁」必須取自同一個來源，混用會讓高亮的
              頁碼跟畫面上的資料對不起來。
            */
            <>
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={curPage <= 1}
                onClick={() => jumpTo(1)}
                aria-label="第一頁"
              >
                «
              </button>
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={curPage <= 1}
                onClick={() => jumpTo(curPage - 1)}
                aria-label="上一頁"
              >
                ‹
              </button>
              {buildPageList(curPage, curPageCount).map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} className="cui-dt-page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`cui-dt-page-btn${p === curPage ? " cui-active" : ""}`}
                    onClick={() => jumpTo(p)}
                    aria-current={p === curPage ? "page" : undefined}
                    // 螢幕報讀器在連續的數字鈕上只會念出「5」，聽不出那是頁碼。
                    // 首／末／上／下四顆本來就有 aria-label，這一排補齊。
                    aria-label={`第 ${p} 頁`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={curPage >= curPageCount}
                onClick={() => jumpTo(curPage + 1)}
                aria-label="下一頁"
              >
                ›
              </button>
              <button
                type="button"
                className="cui-dt-page-btn"
                disabled={curPage >= curPageCount}
                onClick={() => jumpTo(curPageCount)}
                aria-label="最後一頁"
              >
                »
              </button>
            </>
          )}
        </div>
        {jumpCapped && (
          <p className="cui-dt-pager-note">
            {`這份資料共 ${(serverPageCount ?? 0).toLocaleString("zh-Hant-TW")} 頁，頁碼最多只能點到第 ${(serverMaxJump ?? 0).toLocaleString("zh-Hant-TW")} 頁。要查更後面的資料，請先用上方的篩選條件縮小範圍。`}
          </p>
        )}
      </div>
      </div>

      {showColumnSettings && (
        <ColumnSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          columns={columns}
          onToggleVisible={setColumnVisible}
          onMove={moveColumn}
          onReset={resetColumns}
        />
      )}
    </div>
  );
}
